import { OverrideProperties } from 'type-fest';

import { reportException } from '_lib/utils/sentryService';

import {
  CustomQueryHookResult,
  GetByIdResource,
  ListResource,
  PatchHook,
  ResourceId,
  UnpagedListOptions,
  UseCustomMutationOptions,
  UseQueryOptions,
  createApiResource,
  queryKeysAreEqual,
  useCustomMutation,
  useCustomQuery
} from '../apiResourceFactory';
import { API } from '../apiService';
import {
  AiCallDetailDTO,
  AiCallForCallPopDTO,
  AiCallListDTO,
  AiCallQueryParams,
  AiCallUpdateDTO,
  CallOutcomeEnum
} from '../apiTypes.shim';
import { ourQueryClient } from '../QueryProvider';

export type AiCallQueryParamsWithUnknownOutcome = OverrideProperties<
  AiCallQueryParams,
  { outcome?: CallOutcomeEnum | 'UNKNOWN' }
>;

export interface AiCallResource
  extends GetByIdResource<AiCallDetailDTO>,
    ListResource<AiCallListDTO, AiCallQueryParamsWithUnknownOutcome> {
  getAiCallForCallPop: GetByIdResource<AiCallForCallPopDTO>['getById'];
  useCount: (
    options: UnpagedListOptions<AiCallQueryParamsWithUnknownOutcome, { requireParams: true }>,
    queryConfig?: UseQueryOptions<number>
  ) => CustomQueryHookResult<number>;
  useAiCallPatch: PatchHook<AiCallUpdateDTO, AiCallDetailDTO>;
}

export const createAiCallResource = (): AiCallResource => {
  const resourceUri = `ai-call`;

  const { getById, useGetById, getByIdQueryKey, list, useList, listQueryKey } = createApiResource(API, resourceUri);
  const { getById: getAiCallForCallPop } = createApiResource(API, `${resourceUri}/for-call-pop`);

  const useCount = (
    options: UnpagedListOptions<AiCallQueryParamsWithUnknownOutcome, { requireParams: true }>,
    queryConfig?: UseQueryOptions<number>
  ) =>
    useCustomQuery({
      queryKey: [resourceUri, 'count', { params: options.params }],
      queryFn: async () => {
        try {
          const res = await API.get(`${resourceUri}/count`, {
            ...options,
            extraOptions: { retry: 1, ...options?.extraOptions }
          });
          return res.data.count;
        } catch (err) {
          if (!options?.disableErrorReporting) reportException(err);
          return 0;
        }
      },
      ...queryConfig
    });

  type AiCallPatchVariables = { id: ResourceId; data: AiCallUpdateDTO };
  /** TODO: DELETE THIS when we fix the underlying issue where PATCH response is not returning the full AiCallDetailDTO (missing transcript messages) */
  const useAiCallPatch = (
    mutationConfig?: UseCustomMutationOptions<AiCallDetailDTO, unknown, AiCallPatchVariables>
  ) => {
    const context = useCustomMutation<AiCallDetailDTO, unknown, AiCallPatchVariables>({
      mutationFn: async ({ id, data }) => {
        const response = await API.patch(`${resourceUri}/${id}`, data);
        return response.data;
      },
      onSuccess: (data, { id }) => {
        ourQueryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === resourceUri && !queryKeysAreEqual(query.queryKey, getByIdQueryKey({ id })),
          refetchType: 'active'
        });
        ourQueryClient.setQueryData(getByIdQueryKey<AiCallDetailDTO>({ id }), (oldData) => ({ ...oldData, ...data }));
      },

      onError: (error) => reportException(error),
      ...mutationConfig
    });

    return { ...context, patch: context.mutateAsync };
  };

  return {
    getById,
    useGetById,
    getByIdQueryKey,
    list,
    useList,
    listQueryKey,
    getAiCallForCallPop,
    useCount,
    useAiCallPatch
  };
};
